import React, { FunctionComponent, JSX, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import {
  AdminIcon,
  CollapseIcon,
  DashboardIcon,
  ExpandIcon,
  FormIcon,
  MailIcon,
  OfferIcon,
  RegistrationsIcon,
  SettingsIcon,
  SignOutIcon,
  UsersIcon,
  WaitlistIcon
} from "./icons";
import { useAccessibleUsersIds } from "../../../hooks/useAccessibleUsersIds";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useRegistrations } from "../../../hooks/useRegistrations";
import { useUserData } from "../../../hooks/useUserData";
import { useWaitlist } from "../../../hooks/useWaitlist";
import { WebtieWordmark } from "../WebtieWordmark";
import { MOBILE_QUERY, NARROW_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";

interface Props {
  userId: string;
  isAdmin: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Auf Mobile ist die Sidebar ein einschiebbares Menü. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
  signOut: () => Promise<void>;
}

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  badge?: number;
}

export const Sidebar: FunctionComponent<Props> = ({
  userId,
  isAdmin,
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  signOut
}) => {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isNarrow = useMediaQuery(NARROW_QUERY);
  // Auf Mobile immer mit Beschriftung, auf Tablets erzwingt das CSS die schmale Leiste.
  const isCompact = !isMobile && (collapsed || isNarrow);

  // Nach dem Navigieren das mobile Menü schliessen.
  useEffect(() => {
    onCloseMobile();
  }, [pathname]);
  const navigate = useNavigate();
  const { user: authUser } = useAuthUser();
  const { userIds } = useAccessibleUsersIds(authUser?.uid);
  const [registrations] = useRegistrations(userId);
  const [userData] = useUserData(userId);
  const [waitlistEntries] = useWaitlist(userId);
  const isWaitlistEnabled = !!userData?.settings.formSettings?.formSchema?.options?.waitlistEnabled;

  const hasAccessToOtherUsers = userIds.length > 0;
  const base = `/user/${userId}`;

  const mainItems: NavItem[] = [
    { to: `${base}/dashboard`, label: "Dashboard", icon: <DashboardIcon /> },
    {
      to: `${base}/registrations`,
      label: "Anmeldungen",
      icon: <RegistrationsIcon />,
      badge: registrations?.length || undefined
    },
    ...(isWaitlistEnabled
      ? [
          {
            to: `${base}/waitlist`,
            label: "Warteliste",
            icon: <WaitlistIcon />,
            badge: waitlistEntries.length || undefined
          }
        ]
      : []),
    { to: `${base}/limits`, label: "Angebot", icon: <OfferIcon /> },
    { to: `${base}/form`, label: "Formular", icon: <FormIcon /> },
    { to: `${base}/email`, label: "E-Mail", icon: <MailIcon /> }
  ];

  const footItems: NavItem[] = [
    ...(isAdmin ? [{ to: `${base}/admin`, label: "Admin", icon: <AdminIcon /> }] : []),
    ...(hasAccessToOtherUsers ? [{ to: `${base}/user-management`, label: "Formularwahl", icon: <UsersIcon /> }] : []),
    { to: `${base}/settings`, label: "Einstellungen", icon: <SettingsIcon /> }
  ];

  const renderItem = (item: NavItem, muted: boolean): JSX.Element => (
    <Link
      key={item.to}
      to={item.to}
      title={isCompact ? item.label : undefined}
      className={`app-sidebar__item${muted ? " app-sidebar__item--muted" : ""}${
        pathname === item.to ? " is-active" : ""
      }`}>
      {item.icon}
      <span className="app-sidebar__label">{item.label}</span>
      {item.badge !== undefined && <span className="app-sidebar__count app-sidebar__label">{item.badge}</span>}
    </Link>
  );

  return (
    <aside className={`app-sidebar${collapsed ? " is-collapsed" : ""}${mobileOpen ? " is-mobile-open" : ""}`}>
      <Link to={`${base}/dashboard`} className="app-sidebar__brand" aria-label="webtie Startseite">
        <WebtieWordmark size={22} compact={isCompact} />
      </Link>

      <nav className="app-sidebar__nav">{mainItems.map((item) => renderItem(item, false))}</nav>

      <div className="app-sidebar__foot">
        {footItems.map((item) => renderItem(item, true))}

        <button
          type="button"
          title="Abmelden"
          className="app-sidebar__item app-sidebar__item--muted"
          onClick={() => {
            void signOut().then(() => navigate("/"));
          }}>
          <SignOutIcon />
          <span className="app-sidebar__label">Abmelden</span>
        </button>

        <button
          type="button"
          title={collapsed ? "Ausklappen" : "Einklappen"}
          className="app-sidebar__item app-sidebar__item--muted app-sidebar__collapse"
          onClick={onToggleCollapsed}>
          {collapsed ? <ExpandIcon /> : <CollapseIcon />}
          <span className="app-sidebar__label">Einklappen</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
